'use client';

import {
  ControlType,
  CalculatorId,
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type SingleColumnSelectControl,
  type ControlValues
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { CoefficientOfDeterminationCalculator } from './coefficient-of-determination-calculator';
import { calculateCoefficientOfDetermination } from '../../../lib/stats/coefficient-of-determination';

interface CoefficientOfDeterminationControlValues extends ControlValues {
  [ControlIds.XColumn]: string;
  [ControlIds.YColumn]: string;
}

const independentVariable: SingleColumnSelectControl = {
  id: ControlIds.XColumn,
  type: ControlType.SingleColumnSelect,
  label: 'Independent variable (X)',
  required: true,
  numericOnly: true
};

const dependentVariable: SingleColumnSelectControl = {
  id: ControlIds.YColumn,
  type: ControlType.SingleColumnSelect,
  label: 'Dependent variable (Y)',
  required: true,
  numericOnly: true
};

export const coefficientOfDeterminationControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [independentVariable, dependentVariable] as const
    }
  ]
};

export const coefficientOfDeterminationConfig: CalculatorConfig<CalculatorId.COEFFICIENT_OF_DETERMINATION> = {
  id: CalculatorId.COEFFICIENT_OF_DETERMINATION,
  type: CalculatorType.Graph,
  name: 'Coefficient of Determination (R²) Calculator',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'advertising', 
        name: 'Advertising Spend ($)', 
        values: [
          '1000', '2000', '1500', '3000', '2500',
          '3500', '4000', '2000', '1000', '5000'
        ]
      },
      {
        id: 'sales',
        name: 'Sales Revenue ($)',
        values: [
          '5000', '7000', '6000', '9000', '7500',
          '11000', '12000', '7000', '5500', '15000'
        ]
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.XColumn]: 'advertising',
    [ControlIds.YColumn]: 'sales'
  },
  controls: coefficientOfDeterminationControls,
  getInitialConfig: (data: TableDataJSON) => {
    // Find the first two numeric columns
    const numericColumns = data.columns
      .filter(col => col.values.every(val => !isNaN(Number(val))))
      .slice(0, 2)
      .map(col => col.id);

    return {
      [ControlIds.XColumn]: numericColumns[0] || '',
      [ControlIds.YColumn]: numericColumns[1] || ''
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const xColumn = controls[ControlIds.XColumn] as string;
    const yColumn = controls[ControlIds.YColumn] as string;
    if (!xColumn || !yColumn) return null;

    const col1 = data.columns.find(col => col.id === xColumn);
    const col2 = data.columns.find(col => col.id === yColumn);
    
    if (!col1 || !col2) return null;
    
    const data1 = col1.values.map(Number);
    const data2 = col2.values.map(Number);
    
    if (data1.some(isNaN) || data2.some(isNaN)) return null;
    if (data1.length !== data2.length) return null;

    const result = calculateCoefficientOfDetermination({ data1, data2 });
    return {
      ...result,
      xLabel: col1.name || xColumn,
      yLabel: col2.name || yColumn
    };
  },
  render: ({ visualizationData }) => <CoefficientOfDeterminationCalculator visualizationData={visualizationData} />
}; 